import React, { memo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { FormModal } from 'transfix-ui/components/Modals'
import { ToastState } from 'transfix-ui/components/Toast'
import { useDriverApi } from 'apps/fleet-planner/hooks'
import { createDriverRequest } from 'apps/fleet-planner/schema'
import { IDriver } from 'apps/fleet-planner/types'
import { fetchLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { getDefaultEditDriverState } from '../drivers/DriversPage'
import { DriverSettingsModalRow } from './DriverSettingsModalRow'

export interface IValidationErrors {
  // Error messages for each driver at its index
  // e.g ['driver1 message', 'driver2 message']
  drivers: string[]
}
interface IDriverSettingsModalProps {
  isOpen: boolean
  drivers?: IDriver[]
  onClose: () => void
  onSuccess: (toastState: Omit<ToastState, 'toastOpen'>) => void
}

export const DriverSettingsModal = memo<IDriverSettingsModalProps>(
  ({ isOpen, onClose, drivers = [], onSuccess }) => {
    const { id } = useParams<{ id: string }>()
    const dispatch = useDispatch()
    const { error: apiError, updateDriver } = useDriverApi()

    const [formErrors, setFormErrors] = useState<IValidationErrors>({ drivers: [] })

    const cancelModalHandler = () => {
      onClose()
    }

    const setErrorHandler = (errorMessage: string, driverIndex: number) => {
      const newErrors = formErrors.drivers
      newErrors[driverIndex] = errorMessage
      setFormErrors({
        drivers: newErrors,
      })
    }

    const setCellPhoneHandler = (cellPhone: string, driverIndex: number) => {
      const currentDriver = drivers[driverIndex]
      if (currentDriver) {
        drivers[driverIndex] = {
          ...currentDriver,
          cellPhone,
        }
      }
    }

    const setUpdatesToggleHandler = (isToggled: boolean, driverIndex: number) => {
      const currentDriver = drivers[driverIndex]
      if (currentDriver) {
        drivers[driverIndex] = {
          ...currentDriver,
          settings: {
            smsStatusUpdatesEnabled: isToggled,
          },
        }
      }
    }

    const submitDriverRequest = async (d: IDriver) => {
      if (d?.id && !formErrors.drivers.some(e => !!e)) {
        const driver = getDefaultEditDriverState(d)
        const request = createDriverRequest(driver)
        const response = await updateDriver(d.id, request)
        return response
      }
      return undefined
    }

    const submitHandler = async () => {
      drivers.forEach(driver => {
        submitDriverRequest({
          ...driver,
          settings: {
            smsStatusUpdatesEnabled: driver?.settings?.smsStatusUpdatesEnabled ?? false,
            suppressSmsStatusUpdatesOptIn: true,
          },
        }).then(response => {
          if (response) {
            // Fetch load details in order to display updated smsStatusUpdatesEnabled value.
            if (id) dispatch(fetchLoadDetails(id))
          }
        })
      })

      const hasDriverEnabled = drivers.some(
        driver => driver?.settings?.smsStatusUpdatesEnabled === true
      )
      const toastMessage = hasDriverEnabled
        ? `Your ${drivers.length > 1 ? 'drivers' : 'driver'} will be asked for status updates.`
        : 'Driver Notification settings updated.'
      onSuccess({
        toastType: 'default',
        toastMessage,
      })
      onClose()
    }

    return (
      <FormModal
        error={apiError}
        size={4}
        title='Enable driver status updates'
        open={isOpen}
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            action: cancelModalHandler,
          },
          {
            label: 'Save',
            type: 'confirm',
            action: submitHandler,
            disabled: formErrors.drivers.some(e => !!e),
          },
        ]}
        onClose={onClose}>
        <div className='body'>
          Transfix Carrier App will text your driver a timely reminder to update their status.
          You’ll receive an email notification when they arrive and depart each pickup and dropoff.
        </div>
        {drivers.map(d => (
          <DriverSettingsModalRow
            key={d.id}
            driverIndex={drivers.indexOf(d)}
            driver={d}
            formError={formErrors.drivers[drivers.indexOf(d)]}
            onSetErrors={setErrorHandler}
            onSetCellPhone={setCellPhoneHandler}
            onSetUpdatesToggle={setUpdatesToggleHandler}
          />
        ))}
      </FormModal>
    )
  }
)
